import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-xl bg-background-100"
      >
        <div className="relative h-64 md:h-80 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-900/70 via-foreground-900/20 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50 mb-3">
            {article.category}
          </span>
          <h3 className="font-heading text-lg md:text-xl font-bold text-background-50 mb-2 line-clamp-2 group-hover:text-primary-200 transition-colors">
            {article.title}
          </h3>
          <p className="text-background-50/70 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
          <div className="flex items-center gap-3 text-background-50/60 text-xs">
            <span>{article.author}</span>
            <span>{article.publishDate}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-20 h-20 shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h4>
          <p className="text-foreground-500 text-xs mt-1">{article.publishDate} · {article.readTime}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative h-48 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/90 text-foreground-700">
          {article.category}
        </span>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-bold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-sm line-clamp-2 mb-3">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span>{article.author}</span>
          <div className="flex items-center gap-2">
            <span>{article.publishDate}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}